import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useLayer } from './layers'
import styles from './layers.module.css'

const HOUR = 60 * 60 * 1000

// Asks "You're looking at 22nd – 25th Oct" once a page's dates have been off
// its defaults for an hour since they were picked (lib/keptDates.ts), so
// nobody reads an old period as today's. Continue keeps them for another
// hour; the other button puts the page back on its defaults.
export function StillLooking({
  at,
  onDefaults,
  label,
  onContinue,
  onDefault,
  defaultLabel = 'Show today',
}: {
  at: number
  // The page is on its defaults: nothing to ask.
  onDefaults: boolean
  label: string
  onContinue: () => void
  onDefault: () => void
  defaultLabel?: string
}) {
  // Looks again each minute and when the tab comes back into view.
  const [, setTick] = useState(0)
  useEffect(() => {
    const tick = () => setTick((n) => n + 1)
    const id = window.setInterval(tick, 60_000)
    document.addEventListener('visibilitychange', tick)
    return () => {
      window.clearInterval(id)
      document.removeEventListener('visibilitychange', tick)
    }
  }, [])
  if (onDefaults || Date.now() - at < HOUR) return null
  return <Prompt label={label} onContinue={onContinue} onDefault={onDefault} defaultLabel={defaultLabel} />
}

function Prompt({ label, onContinue, onDefault, defaultLabel }: { label: string; onContinue: () => void; onDefault: () => void; defaultLabel: string }) {
  const box = useRef<HTMLDivElement>(null)
  useLayer(true, onContinue, { el: box, modal: true })
  const go = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    go.current?.focus()
  }, [])
  return createPortal(
    <div
      className={styles.scrim}
      onClick={(e) => {
        if (e.target === e.currentTarget) onContinue()
      }}
    >
      <div ref={box} className={styles.box} role="alertdialog" aria-modal="true" aria-labelledby="dp-still-q">
        <p id="dp-still-q" className={styles.q}>
          You're looking at {label}
        </p>
        <div className={styles.actions}>
          <button type="button" className={styles.keep} onClick={onContinue}>
            Continue
          </button>
          <button ref={go} type="button" className={styles.go} onClick={onDefault}>
            {defaultLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}
